import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { Children, Fragment, createElement, createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import * as RBDND from 'react-beautiful-dnd';
import mitt from 'mitt';
import StackView from '../StackView';
var DragDropContext = /*#__PURE__*/createContext(false);
var DroppableContext = /*#__PURE__*/createContext(false);
var emitter = mitt();
var draggableData = {};
var portalNode = null;
var providerCount = 0;

function setDraggableData(id, data) {
  draggableData[id] = data;
  return function () {
    delete draggableData[id];
  };
}

function useDragDrop() {
  var hasParentDragDrop = useContext(DragDropContext);
  return hasParentDragDrop;
}

function Provider(_ref) {
  var _onDragStart = _ref.onDragStart,
      _onDragEnd = _ref.onDragEnd,
      props = _objectWithoutPropertiesLoose(_ref, ["onDragStart", "onDragEnd"]);

  var hasParentDragDrop = useDragDrop();

  if (typeof document !== 'undefined' && portalNode === null) {
    portalNode = document.createElement('div');
    portalNode.id = 'rbd-portal';
    document.body.appendChild(portalNode);
  }

  useEffect(function () {
    providerCount += 1;
    return function () {
      providerCount -= 1; // last unmounting provider clears the portalNode

      if (providerCount === 0) {
        portalNode = null;
      }
    };
  }, []);
  return hasParentDragDrop ? props.children : /*#__PURE__*/React.createElement(DragDropContext.Provider, {
    value: true
  }, /*#__PURE__*/React.createElement(RBDND.DragDropContext, _extends({
    onDragStart: function onDragStart(event) {
      var augmentedEvent = _objectSpread(_objectSpread({}, event), {}, {
        data: draggableData[event.draggableId] || null
      });

      emitter.emit('dragStart', augmentedEvent);

      if (_onDragStart) {
        _onDragStart(augmentedEvent);
      }
    },
    onDragEnd: function onDragEnd(event) {
      var augmentedEvent = _objectSpread(_objectSpread({}, event), {}, {
        data: draggableData[event.draggableId] || null
      });

      emitter.emit('dragEnd', augmentedEvent);

      if (_onDragEnd) {
        _onDragEnd(augmentedEvent);
      }
    }
  }, props)));
}

function Droppable(_ref2) {
  var children = _ref2.children,
      sourceOnly = _ref2.sourceOnly,
      onDragStart = _ref2.onDragStart,
      onDragEnd = _ref2.onDragEnd,
      onDrop = _ref2.onDrop,
      props = _objectWithoutPropertiesLoose(_ref2, ["children", "sourceOnly", "onDragStart", "onDragEnd", "onDrop"]);

  useEffect(function () {
    function handleDragStart(event) {
      if (onDragStart && event.source.droppableId === props.droppableId) {
        onDragStart(event);
      }
    }

    emitter.on('dragStart', handleDragStart);
    return function () {
      emitter.off('dragStart', handleDragStart);
    };
  }, [onDragStart]);
  useEffect(function () {
    function handleDragEnd(event) {
      if (onDragEnd && event.source.droppableId === props.droppableId) {
        onDragEnd(event);
      }

      if (onDrop && event.destination && event.destination.droppableId === props.droppableId) {
        onDrop(event);
      }
    }

    emitter.on('dragEnd', handleDragEnd);
    return function () {
      emitter.off('dragEnd', handleDragEnd);
    };
  }, [onDragEnd, onDrop]);
  return /*#__PURE__*/React.createElement(DroppableContext.Provider, {
    value: sourceOnly
  }, /*#__PURE__*/React.createElement(RBDND.Droppable, _extends({
    isDropDisabled: sourceOnly
  }, props), function (provided, snapshot) {
    if (sourceOnly) {
      provided.placeholder = /*#__PURE__*/React.createElement("div", {
        style: {
          display: 'none'
        }
      }, provided.placeholder);
    }

    return children(provided, snapshot);
  }));
}

function Draggable(_ref3) {
  var children = _ref3.children,
      placeholder = _ref3.placeholder,
      data = _ref3.data,
      props = _objectWithoutPropertiesLoose(_ref3, ["children", "placeholder", "data"]);

  var sourceOnly = useContext(DroppableContext);
  useEffect(function () {
    return setDraggableData(props.draggableId, data);
  }, [data]);
  return /*#__PURE__*/React.createElement(RBDND.Draggable, props, function (provided, snapshot) {
    if (sourceOnly) {
      provided.draggableProps.style = snapshot.isDragging ? provided.draggableProps.style : {
        transform: 'none',
        transition: 'none'
      };
    }

    var child = children(provided, snapshot);
    return snapshot.isDragging ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/createPortal(child, portalNode), sourceOnly && placeholder && placeholder()) : child;
  });
}

function StaticList(_ref4) {
  var children = _ref4.children,
      _variants = _ref4.variants,
      id = _ref4.id,
      props = _objectWithoutPropertiesLoose(_ref4, ["children", "variants", "id"]);

  var variants = _objectSpread({
    item: function item(style) {
      return style;
    },
    placeholder: {
      opacity: 0.5
    }
  }, _variants);

  return /*#__PURE__*/React.createElement(Droppable, {
    droppableId: id,
    isDropDisabled: true
  }, function (provided) {
    return /*#__PURE__*/React.createElement(StackView, _extends({
      innerRef: provided.innerRef
    }, props), Children.map(children, function (child, index) {
      var _child$props = child.props,
          id = _child$props.id,
          childProps = _objectWithoutPropertiesLoose(_child$props, ["id"]);

      return /*#__PURE__*/React.createElement(Draggable, {
        key: child.key,
        draggableId: id,
        index: index
      }, function (provided, snapshot) {
        return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/createElement(child.type, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, childProps), provided.draggableProps), provided.dragHandleProps), {}, {
          innerRef: provided.innerRef,
          style: variants.item(snapshot.isDragging ? provided.draggableProps.style : {
            transform: 'none',
            transition: 'none'
          }, snapshot)
        })), snapshot.isDragging && /*#__PURE__*/createElement(child.type, _objectSpread(_objectSpread({}, childProps), variants.placeholder)));
      });
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'none'
      }
    }, provided.placeholder));
  });
}

export default {
  Provider: Provider,
  Droppable: Droppable,
  Draggable: Draggable,
  StaticList: StaticList,
  useDragDrop: useDragDrop
};